import LevelBar from '@app/src/components/LevelBar'
import TruncateWithDialog from '@app/src/components/Ui/TruncateWithDialog'
import { getLevelDescription, getLevelLabel } from '@app/src/pages/Assessments/Assessment/utils'
import type { AssessmentTemplate } from '@app/src/types/resourceExplorer'
import { getAssessmentLevelConfig, getLevelThresholdRanges } from '@app/src/utils/assessmentLevels'
import { LabelFormat } from '@app/src/utils/assessmentLevelTranslations'
import { Box, Grid, Typography, useTheme } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

interface AssessmentMethodologyCardProps {
  assessmentTemplate: AssessmentTemplate
  isTransparent?: boolean
}

const AssessmentMethodologyCard: React.FC<AssessmentMethodologyCardProps> = ({
  assessmentTemplate,
  isTransparent = false,
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const levels = assessmentTemplate.levels ?? 5

  const thresholdRanges = getLevelThresholdRanges(assessmentTemplate.levelThresholdPercentages)

  const getColorSchemes = () => {
    const levelConfig = getAssessmentLevelConfig(theme, formatMessage)
    if (levels === 3) {
      return [
        { background: levelConfig.poor.bgColor },
        { background: levelConfig.fair.bgColor },
        { background: levelConfig.good.bgColor },
      ]
    }
    return [
      { background: levelConfig.veryPoor.bgColor },
      { background: levelConfig.poor.bgColor },
      { background: levelConfig.fair.bgColor },
      { background: levelConfig.good.bgColor },
      { background: levelConfig.excellent.bgColor },
    ]
  }

  const getLevelDescriptionText = (level: number) => {
    return getLevelDescription(level, assessmentTemplate, undefined, formatMessage)
  }

  const getLevelLabelText = (level: number) => {
    return getLevelLabel(
      level,
      levels,
      assessmentTemplate.assessmentType,
      assessmentTemplate.levelNamesType,
      formatMessage,
      LabelFormat.Basic,
    )
  }

  const colorSchemes = getColorSchemes()

  return (
    <Grid container spacing={1} sx={{ mb: 4 }}>
      {Array.from({ length: levels }).map((_, index) => (
        <Grid item xs={12} lg={levels === 3 ? 4 : 2.4} key={`level-${index}`}>
          <Box
            width="100%"
            height="100%"
            pt={3}
            pb={4}
            px={3}
            bgcolor={colorSchemes[index].background}
            borderRadius={2}
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            gap={1}
          >
            <Box>
              <LevelBar
                value={index + 1}
                levelsCount={levels}
                assessmentType={assessmentTemplate.assessmentType}
                levelNamesType={assessmentTemplate.levelNamesType}
                thresholds={assessmentTemplate.levelThresholdPercentages}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: 12,
                lineHeight: '24px',
                mb: 1,
                fontFamily: 'Poppins',
                color: theme.palette.text.primary,
              }}
            >
              {formatMessage(
                { id: 'assessments.methodology.levelsPercentage' },
                {
                  from: thresholdRanges[index].from,
                  to: thresholdRanges[index].to,
                },
              )}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: 14,
                lineHeight: '24px',
                fontFamily: 'Poppins',
                color: theme.palette.text.primary,
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                hyphens: 'auto',
              }}
            >
              <TruncateWithDialog
                title={getLevelLabelText(index + 1)}
                maxLength={isTransparent ? 100 : 200}
                text={getLevelDescriptionText(index + 1)}
              />
            </Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  )
}

export default AssessmentMethodologyCard
